import type { EventRow } from './types';

/**
 * Firestore 문서 → `EventRow`.
 *
 * ★★★ **필드명에 의존하는 유일한 지점이다**(원본 `lensMapEventDocs` 와 같은
 * 자리). 실제 필드명이 다르면 **이 함수 하나만** 고치면 된다 — 화면과 집계는
 * 손대지 않는다.
 *
 * ⚠️⚠️ **FF 게터의 「접힘」을 그대로 흉내 낸다.** FF 는 값이 없으면
 * `''`/`0`/`false` 로 접는다. 여기서 `undefined` 를 흘리면 집계가 조용히
 * 달라진다 — 예컨대 `parseOk` 가 `undefined` 면 `=== false` 가 거짓이라
 * **진행 불가 분자에서 빠진다.**
 *
 * ★ 예외는 `narrationValidated` 하나다. 키가 없으면 `null` 이어야
 *   「검증이 안 돎」과 「검증이 돌고 성공」이 구분된다(`hasX()` 의 짝).
 *
 * ⚠️ 리스트 4종(`player_states` 등)은 원본도 매핑하지 않는다 — 읽는 화면이
 *    하나도 없고, `viable_approaches` 는 **객체 배열**이라 `string[]` 로
 *    캐스팅하면 **문서 파싱 단계에서 앱 전체가 죽는다**(실측).
 */

type Doc = Record<string, unknown>;

const s = (d: Doc, k: string): string => (typeof d[k] === 'string' ? (d[k] as string) : '');
const n = (d: Doc, k: string): number => (typeof d[k] === 'number' ? (d[k] as number) : 0);
const b = (d: Doc, k: string): boolean => d[k] === true;

/** Firestore `Timestamp` · ISO 문자열 · `Date` 를 모두 받는다. */
function toDate(v: unknown): Date | null {
  if (v === null || v === undefined) return null;
  if (v instanceof Date) return v;
  if (typeof v === 'object' && 'toDate' in v && typeof (v as { toDate: unknown }).toDate === 'function') {
    return (v as { toDate: () => Date }).toDate();
  }
  if (typeof v === 'string') {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

export function mapEventDoc(d: Doc): EventRow {
  return {
    eventName: s(d, 'event_name'),
    runId: s(d, 'run_id'),
    campaignId: s(d, 'campaign_id'),
    chapterId: s(d, 'chapter_id'),
    sceneId: s(d, 'scene_id'),
    contentVersion: s(d, 'content_version'),
    modelVersion: s(d, 'model_version'),
    promptVersion: s(d, 'prompt_version'),
    appVersion: s(d, 'app_version'),
    occurredAt: toDate(d['occurred_at']),
    exitReason: s(d, 'exit_reason'),
    kind: s(d, 'kind'),
    dc: n(d, 'dc'),
    rollResult: n(d, 'roll_result'),
    success: b(d, 'success'),
    actionType: s(d, 'action_type'),
    parseOk: b(d, 'parse_ok'),
    attempts: n(d, 'attempts'),
    reason: s(d, 'reason'),
    regenerated: b(d, 'regenerated'),
    usedFallback: b(d, 'used_fallback'),
    durationSec: n(d, 'duration_sec'),
    turnCount: n(d, 'turn_count'),
    playerId: s(d, 'player_id'),
    turnIndex: n(d, 'turn_index'),
    endingId: s(d, 'ending_id'),
    endingKind: s(d, 'ending_kind'),
    costUsd: n(d, 'cost_usd'),
    promptTokens: n(d, 'prompt_tokens'),
    completionTokens: n(d, 'completion_tokens'),
    durationMs: n(d, 'duration_ms'),
    fallbackReason: s(d, 'fallback_reason'),
    judgmentKind: s(d, 'judgment_kind'),
    alertLevel: n(d, 'alert_level'),
    pursuitMargin: n(d, 'pursuit_margin'),
    playerStates: [],
    sceneConditions: [],
    sideEffects: [],
    flagId: s(d, 'flag_id'),
    flagKind: s(d, 'flag_kind'),
    leakedTag: s(d, 'leaked_tag'),
    propId: s(d, 'prop_id'),
    rejectedAttempts: n(d, 'rejected_attempts'),
    viableApproaches: [],
    requestedPropId: s(d, 'requested_prop_id'),
    // ★ 1.4.0 부터. 없으면 빈 문자열 — 「없음」과 「impossible」은 둘 다
    //   `!== 'unclear'` 라 재현 규칙에서 같은 쪽으로 간다(회신 46 1-1).
    noActionReason: s(d, 'no_action_reason'),
    schemaVersion: s(d, 'schema_version'),
    narrationValidated:
      'narration_validated' in d ? (d['narration_validated'] as boolean) : null,
  };
}

export const mapEventDocs = (docs: Doc[]): EventRow[] => docs.map(mapEventDoc);

/**
 * `casual_play` 는 `EventRow` 에 없다 — **가져올 때만** 쓰는 거르개다.
 * ★ 원본도 `EventsRecord` 에서만 읽는다(struct 에는 자리가 없다).
 */
/**
 * ⚠️⚠️ **이벤트가 틀리게 나간 회차** — 개발 회신 40 2절(2026-10-04).
 *
 * `casual_play` 는 **발행 시점의 컬럼값으로 굳는다**(`casualPlay: loaded.casualPlay`,
 * `server/src/api/turnsRoute.ts`). ★ 그래서 OBXYD 가 나중에 DB 를 `true` 로
 * 고쳐도 **이미 나간 이벤트는 `false` 그대로**이고, Firestore 에 그 회차를
 * 다시 쓰는 경로가 없다.
 *
 * ★ 아래 둘은 2026-10-03 대화 판 실측 세션이다 — 정기 회차가 아니다.
 *   OBXYD 가 DB 를 고쳤고(UPDATE 2), **LENS 쪽에서 빼 달라**고 했다.
 *
 * ⚠️ **보이지 않는 필터로 두지 않는다** — 제외한 수가 설정의 「데이터 소스」에
 * 한 줄로 뜬다. 이 프로젝트는 「조용히 자르면 없었던 것으로 읽힌다」로 여러 번
 * 데였다.
 *
 * ⚠️ 목록이 길어지면 설정으로 옮긴다. 둘뿐이라 코드에 둔다 — 설정에 두면
 * 브라우저마다 값이 갈려 **대조가 깨진다.**
 */
export const MISLABELED_CASUAL_RUNS: readonly string[] = [
  // ★ 2026-10-03 대화 판 실측 세션 둘 — 이벤트에 `casual_play: false` 로 **틀리게 실렸다**.
  'sess_probe_v6_01',
  'sess_v6probe_run2',
  // ★ 2026-09-30 사용자의 임의 플레이 — 개발 회신 42 3절(사용자 확정 2026-10-04).
  //   ⚠️ 위 둘과 **이유가 다르다**: 이 회차는 `casual_play` 가 **아예 없다**
  //   (Firestore 19건 전부 `undefined` — 필드 발행 전이다). `=== true` 가
  //   없음을 거짓으로 접으므로 목록만이 가른다.
  'sess_493ecced273c4bedb10f22ce5f96a9a4',
  // 2026-10-06 — DB 에서 뒤늦게 true 로 고쳤다(개발 커밋 437e2f7). 1턴이라
  //   「쓸 만한 회차」에서 어차피 빠지지만 **목록에 이름을 둔다**(회신 48 4절).
  'sess_db22b59edad64bb8a9819d23c9aa21cc',
];

/**
 * 임의 플레이인가.
 *
 * ★ 이벤트의 `casual_play` 가 1차다. ⚠️ 그것이 틀리게 나간 회차는
 * `MISLABELED_CASUAL_RUNS` 가 받는다.
 */
export const isCasualPlay = (d: Doc): boolean =>
  d['casual_play'] === true ||
  MISLABELED_CASUAL_RUNS.includes((d['run_id'] as string | undefined) ?? '');
